import { FC, ReactNode } from 'react';
import type { UsageBreakdownRow } from '@libs/types';
import { Box, Skeleton, Typography, useTheme } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { useTrans } from '@libs/i18n';

import { AdminPanel, AdminPanelHead } from '../AdminPanel';
import { adminColors } from '../adminTheme';

import { formatCompactCount, formatUsd } from './usageFormat';
import { UsageMeter } from './UsageMeter';
import { UsageNumber } from './UsageNumber';

interface Props {
  icon?: ReactNode;
  title: string;
  sub?: string;
  rows: UsageBreakdownRow[];
  isLoading?: boolean;
  /** Maps a raw `service`/`feature` key to a translated label. */
  labelFor: (key: string) => string;
  color: string;
}

/**
 * Sorted cost breakdown as bar-meter rows.
 *
 * Deliberately not a pie or donut. The categories span several orders of
 * magnitude (llm at $381 next to email at $0.02); as arc slices most are
 * invisible and none carry a readable figure. A sorted list with the exact
 * amount beside each bar is what an admin actually reads, needs no chart
 * library, and mirrors correctly in RTL for free.
 */
export const UsageBreakdownPanel: FC<Props> = observer(
  ({ icon, title, sub, rows, isLoading, labelFor, color }) => {
    const { t } = useTrans();
    const theme = useTheme();
    const c = adminColors(theme);

    // Share of the largest row, not of the total: with a dominant category
    // every other bar would round to nothing against the total.
    const max = rows.reduce((m, r) => Math.max(m, r.costUsd), 0);

    return (
      <AdminPanel>
        <AdminPanelHead icon={icon} iconColor={color} title={title} sub={sub} />
        <Box sx={{ px: 2.25, py: 2, display: 'flex', flexDirection: 'column', gap: 1.625 }}>
          {isLoading &&
            [0, 1, 2, 3].map((i) => (
              <Skeleton key={i} variant="rounded" height={34} sx={{ borderRadius: 2 }} />
            ))}

          {!isLoading && rows.length === 0 && (
            <Typography sx={{ fontSize: 12.5, color: c.faint, py: 1 }}>
              {t('app.admin.usage.breakdown.empty')}
            </Typography>
          )}

          {!isLoading &&
            rows.map((r) => (
              <UsageMeter
                key={r.key}
                label={labelFor(r.key)}
                value={<UsageNumber>{formatUsd(r.costUsd)}</UsageNumber>}
                ratio={max > 0 ? r.costUsd / max : 0}
                color={color}
                caption={t('app.admin.usage.breakdown.calls', {
                  calls: formatCompactCount(r.calls),
                })}
              />
            ))}
        </Box>
      </AdminPanel>
    );
  },
);
